/**
 * Phase 37 — the drops that are a bet (scaled, shrunk, a list's first day,
 * a returning list, an experiment's drop) and the engine-made ones (extra,
 * probe) as one small grid: a caption line, then one line per kind — same
 * as the rest · a different mix · the texts on each such drop · today's drops of
 * that kind — and one button for the recommended policy (bets send proven
 * texts only). Every explanation sits behind a ⓘ.
 * Hidden on the user's word (15/10/2026): only the hit-again line shows; the
 * bet lines, their caption and the recommended button come back by passing
 * `kinds` — the engine still honours a policy saved before.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { DROP_GEN_POLICY_KINDS, type DropGenPolicyKind, type DropGenTextMix, type DropGenTextMixInput, type DropGenTextPolicy, RECOMMENDED_TEXT_POLICY, sameMix } from '@trafficpulse/shared';

import { Button, HintDot } from '@/components/ui';
import { cn } from '@/lib/utils';

import { useCanSeeExperiments } from '../../../../shared/experiment/use-can-see-experiments';
import type { TodayFacts } from './today/kind-facts';
import { POLICY_COLS, POLICY_ROW, PolicyRow } from './today/PolicyRow';

const HINT =
  'Proven texts where the drop is a bet, fresh AI texts where it is safe. A scaled drop pairs a bigger stake with the surest texts; a list’s first day or its return is judged on the list, not on text luck. The ordinary drops keep some fresh texts so the ranking keeps learning. "A different mix" (on the hit-again line: "new texts") = what each such drop sends: ★ proven texts from the reuse pool (the best first, taking turns) + ✦ fresh AI texts, on the same drop, 15 at most. If the pool has fewer proven texts than asked, the missing ones become fresh AI texts automatically — the row then shows what really goes out. The recommended policy: the bets send the 5 best proven texts and nothing fresh. A draft that becomes one of these kinds mid-day gets its proven texts added in front of its own, at no AI cost.';

const CAPTION = 'text-[10px] font-semibold uppercase tracking-wider text-muted-foreground';

/** The lines shown today — the bets are hidden for now (see the header). */
export const SHOWN_POLICY_KINDS: readonly DropGenPolicyKind[] = ['HIT_AGAIN'];

interface Props {
  /** The ordinary mix and the kinds' own. */
  cfg: DropGenTextMixInput;
  onChange: (policy: DropGenTextPolicy) => void;
  /** Today's drops with this policy applied; null = no day laid out yet. */
  today?: TodayFacts | null;
  /** The reuse pool's size; null while the ranking is being read. */
  poolSize?: number | null;
  /** The kinds on the grid; the caption and the recommended button come with the bets. */
  kinds?: readonly DropGenPolicyKind[];
}

export const PolicyRows: FC<Props> = ({ cfg, onChange, today = null, poolSize = null, kinds = SHOWN_POLICY_KINDS }) => {
  const canSeeExperiments = useCanSeeExperiments(); // the experiment row: admin-only unless granted
  const policy = cfg.policy ?? {};
  const set = (kind: DropGenPolicyKind, value: DropGenTextMix | undefined) => {
    const next = { ...policy };
    if (value === undefined) delete next[kind];
    else next[kind] = value;
    onChange(next);
  };
  const recommended = DROP_GEN_POLICY_KINDS.every((k) => {
    const want = RECOMMENDED_TEXT_POLICY[k];
    const have = policy[k];
    return want === undefined ? have === undefined : have !== undefined && sameMix(have, want);
  });
  return (
    <div className="flex flex-col gap-1" data-testid="text-policy">
      {kinds.some((k) => k !== 'HIT_AGAIN') ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className={cn('flex items-center gap-1.5', CAPTION, 'text-[10.5px]')}>
            Drops that are a bet
            <HintDot label={HINT} />
          </span>
          <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs" disabled={recommended} onClick={() => onChange({ ...RECOMMENDED_TEXT_POLICY })}>
            {recommended ? 'Recommended policy in use' : 'Use the recommended policy'}
          </Button>
        </div>
      ) : null}
      <div className={POLICY_COLS}>
        <div className={cn(POLICY_ROW, 'hidden pb-1 pt-2 sm:grid')} aria-hidden>
          <span />
          <span className={cn(CAPTION, 'sm:col-span-2')}>texts on each drop</span>
          <span className={CAPTION}>today</span>
        </div>
        {kinds.filter((k) => k !== 'EXPERIMENT' || canSeeExperiments).map((kind, i) => (
          <PolicyRow key={kind} kind={kind} cfg={cfg} index={i} onSet={(v) => set(kind, v)} drops={today ? today.drops.filter((d) => d.kind === kind) : null} poolSize={poolSize} />
        ))}
      </div>
    </div>
  );
};
